import { useCallback, useMemo } from 'react'

import { useQueries } from '@tanstack/react-query'
import { AgChartOptions } from 'ag-charts-community'
import { CellValueChangedEvent, GetRowIdParams } from 'ag-grid-enterprise'
import { isAfter, isWeekend } from 'date-fns'
import { Check, Play } from 'lucide-react'

import { getTMinus1, toISO8601 } from '@fortem-monorepo/js-utilities'
import { numberFormatter } from '@fortem-monorepo/js-utilities'
import { createColDefs } from '@fortem-monorepo/react-ag'
import { CustomAgChart } from '@fortem-monorepo/react-ag'
import { CustomAgGrid } from '@fortem-monorepo/react-ag'
import { FiltersProvider, useApi, useFilters } from '@fortem-monorepo/react-spa'
import { createFilters } from '@fortem-monorepo/react-spa'
import { HeaderConfigBar } from '@fortem-monorepo/react-spa'
import { Widget, WidgetContent, WidgetHeaderWithQuery } from '@fortem-monorepo/react-spa'
import { useConfirmModal } from '@fortem-monorepo/react-spa'
import { useUserContext } from '@fortem-monorepo/react-spa'
import { DatePickerWithControls } from '@fortem-monorepo/react-ui'
import { LoadingButton } from '@fortem-monorepo/react-ui'
import { SearchSelect } from '@fortem-monorepo/react-ui'
import { FormItem } from '@fortem-monorepo/react-ui'
import { Label } from '@fortem-monorepo/react-ui'
import { ResizableHandle, ResizablePanel, ResizablePanelGroup } from '@fortem-monorepo/react-ui'
import { Attributions } from '@fortem-monorepo/web-sdk'

import { CanAccess } from '@/components/CanAccess'
import { Page } from '@/components/Page'
import { PM } from '@/constants/links'
import { PERMISSIONS } from '@/constants/permissions'
import {
  useRunRiskPremiaMutation,
  useUpdatePnlDiscrepancyMutation,
  useValidateFinalAttributionsMutation,
} from '@/react-query/mutations/attributions'
import { useFunds } from '@/react-query/queries/funds/funds'

const pnlColDefs = (hasWriteAccess: boolean) =>
  createColDefs([
    {
      field: 'value_date',
      headerName: 'Value Date',
      width: 110,
    },
    {
      field: 'fund_name',
      headerName: 'Fund',
      width: 100,
    },
    {
      field: 'structurename',
      headerName: 'Product',
      width: 175,
    },
    {
      field: 'value_ops',
      headerName: 'Ops',
      width: 115,
      cellStyle: { textAlign: 'center' },
      valueFormatter: numberFormatter({
        delimiter: true,
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }),
    },
    {
      field: 'value_rec',
      headerName: 'Rec',
      width: 115,
      cellStyle: { textAlign: 'center' },
      valueFormatter: numberFormatter({
        delimiter: true,
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }),
    },
    {
      field: 'use_ops',
      headerName: 'Use Ops',
      width: 125,
      editable: hasWriteAccess,
    },
    {
      field: 'insertion_datetime_utc',
      headerName: 'Insertion UTC',
      hide: false,
      width: 150,
    },
  ])

const securitiesColDefs = createColDefs([
  {
    width: 170,
    field: 'unique_to_ops',
  },
  {
    width: 170,
    field: 'unique_to_rec',
  },
])

const finalAttributionsColDefs = createColDefs([
  {
    field: 'asatdate',
    headerName: 'Value Date',
    width: 120,
  },
  {
    field: 'fundname',
    headerName: 'Fund',
    width: 90,
  },
  {
    field: 'securityref',
    headerName: 'Product',
    width: 185,
  },
  {
    field: 'dailyimpact',
    headerName: 'Daily Impact',
    width: 150,
    cellStyle: { textAlign: 'center' },
    valueFormatter: numberFormatter({
      delimiter: true,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }),
  },
])

const getChartOptions = (data: Attributions.GetChartData) => {
  return {
    height: 400,
    data,
    legend: {
      item: {
        marker: {
          shape: 'square',
          strokeWidth: 0,
        },
      },
    },
    series: [
      {
        type: 'bar',
        xKey: 'securityref',
        yKey: 'weighted_bbg_returns_bps',
        yName: 'Wgt BBG Rtn Bps',
      },
      {
        type: 'bar',
        xKey: 'securityref',
        yKey: 'returns_ops_bps',
        yName: 'Ops Rtn Bps',
      },
      {
        type: 'bar',
        xKey: 'securityref',
        yKey: 'returns_rec_bps',
        yName: 'Rec Rtn Bps',
      },
    ],
    axes: [
      {
        type: 'category',
        position: 'bottom',
      },
      {
        type: 'number',
        position: 'left',
        keys: ['weighted_bbg_returns_bps', 'returns_ops_bps', 'returns_rec_bps'],
      },
    ],
  } satisfies AgChartOptions
}

const filters = createFilters([
  {
    name: 'value_date',
    label: 'Report Date',
    type: 'date',
    defaultValue: getTMinus1(),
    params: true,
  },
  {
    name: 'fund_name',
    label: 'Fund Name',
    type: 'select',
    defaultValue: 'all',
    params: true,
  },
])

export interface RiskPremiaPageProps {}

export const RiskPremiaPage: React.FC<RiskPremiaPageProps> = ({}) => {
  return (
    <Page metadata={{ title: 'Risk Premia' }} permissions={PM.ATTRIBUTIONS.RISK_PREMIA.permissions}>
      <FiltersProvider filters={filters}>
        <RiskPremia />
      </FiltersProvider>
    </Page>
  )
}

export interface RiskPremiaProps {}

export const RiskPremia: React.FC<RiskPremiaProps> = ({}) => {
  const { registerFilter, values } = useFilters<typeof filters>()

  const { hasPermissions } = useUserContext()

  const hasWriteAccess = hasPermissions((permissions) => ({
    permissions: [permissions.WRITE_RISK_PREMIA],
  }))

  const { data: funds } = useFunds()

  const fundsOptions = useMemo(() => {
    return [
      { label: 'Show all', value: 'all' },
      ...funds
        .filter((fund) => fund.active)
        .map((fund) => ({ label: fund.fund_name, value: fund.fund_name })),
    ]
  }, [funds])

  const params: Attributions.GetDiscrepanciesParams = {
    fund_name: values.fund_name!,
    value_date: values.value_date ? toISO8601(values.value_date) : '',
  }

  const api = useApi()

  const queries = useQueries({
    queries: [
      {
        queryKey: ['attributions', 'risk-premia', 'pnl', params],
        queryFn: () => api.attributions.riskPremia.getPNLdiscrepancies(params),
      },
      {
        queryKey: ['attributions', 'risk-premia', 'securities', params],
        queryFn: () => api.attributions.riskPremia.getSecuritiesdiscrepancies(params),
      },
      {
        queryKey: ['attributions', 'risk-premia', 'final-attributions', params],
        queryFn: () => api.attributions.riskPremia.getFinalAttributions(params),
      },
      {
        queryKey: ['attributions', 'risk-premia', 'chart', params],
        queryFn: () => api.attributions.riskPremia.getChart(params),
      },
    ],
  })

  const [pnl, securities, finalAttributions, chart] = queries

  const [pnlData, securitiesData, finalAttributionsData, chartData] = useMemo(() => {
    return [pnl.data ?? [], securities.data ?? [], finalAttributions.data ?? [], chart.data ?? []]
  }, [pnl, securities, finalAttributions, chart])

  // const queriesAreFetching = queries.some((query) => query.isFetching)

  const runRiskPremiaMutation = useRunRiskPremiaMutation()
  const validateFinalAttributionsMutation = useValidateFinalAttributionsMutation()
  const updatePnlDiscrepancyMutation = useUpdatePnlDiscrepancyMutation()

  const { Dialog: RunRiskPremiaDialog, withConfirmation: confirmRunRiskPremia } = useConfirmModal({
    title: 'Run Risk Premia',
    description: (
      <div>
        Confirm you want to Run Risk Premia for:
        <ul className={'block list-disc pl-6 my-2'}>
          <li>
            Fund: <span className={'font-semibold'}>{params.fund_name}</span>
          </li>
          <li>
            Value Date: <span className={'font-semibold'}>{params.value_date}</span>
          </li>
        </ul>
        <div>
          The Process takes ~15s. Make sure:
          <ul className={'block list-disc pl-6 my-2'}>
            <li>Cash Flows.xlsx has been uploaded</li>
            <li>Banks/Custodians runs have been extracted</li>
          </ul>
        </div>
      </div>
    ),
  })

  const {
    Dialog: ValidateFinalAttributionsDialog,
    withConfirmation: confirmValidateFinalAttributions,
  } = useConfirmModal({
    title: 'Validate Final Attributions',
    description: (
      <div>
        Confirm you want to validate Final Attributions for:
        <ul className={'block list-disc pl-6 my-2'}>
          <li>
            Fund: <span className={'font-semibold'}>{params.fund_name}</span>
          </li>
          <li>
            Value Date: <span className={'font-semibold'}>{params.value_date}</span>
          </li>
        </ul>
        <span>
          Running this process overrides previous validated data for that fund and value date.
        </span>
      </div>
    ),
  })

  const runRiskPremia = useCallback(
    () => confirmRunRiskPremia(() => runRiskPremiaMutation.mutateAsync(params)),
    [runRiskPremiaMutation, params],
  )

  const validateFinalAttributions = useCallback(
    () =>
      confirmValidateFinalAttributions(() => validateFinalAttributionsMutation.mutateAsync(params)),
    [runRiskPremiaMutation, params],
  )

  return (
    <>
      <ValidateFinalAttributionsDialog />
      <RunRiskPremiaDialog />
      <HeaderConfigBar>
        <div className="flex items-end gap-2">
          <FormItem>
            <Label className={'text-xs text-muted-foreground'}>Report Date</Label>
            <DatePickerWithControls
              size={'sm'}
              disabled={(date) => isWeekend(date) || isAfter(date, new Date())}
              {...registerFilter('value_date')}
            />
          </FormItem>
          <FormItem>
            <Label className={'text-xs text-muted-foreground'}>Fund</Label>
            <SearchSelect
              size="sm"
              className={'min-w-40'}
              options={fundsOptions}
              {...registerFilter('fund_name')}
            />
          </FormItem>
          <CanAccess permissions={[PERMISSIONS.WRITE_RISK_PREMIA]}>
            <LoadingButton
              size="sm"
              variant="outline"
              onClick={async () => runRiskPremia()}
              icon={<Play />}
            >
              Run Risk Premia
            </LoadingButton>
            <LoadingButton
              size="sm"
              variant="outline"
              onClick={async () => validateFinalAttributions()}
              icon={<Check />}
            >
              Validate Final Attributions
            </LoadingButton>
          </CanAccess>
        </div>
      </HeaderConfigBar>
      <ResizablePanelGroup direction={'vertical'}>
        <ResizablePanel>
          <ResizablePanelGroup direction={'horizontal'}>
            <ResizablePanel>
              <Widget exposed className={'h-full'}>
                <WidgetHeaderWithQuery
                  title={`PNL discrepancies - ${values.fund_name}`}
                  query={pnl}
                />
                <WidgetContent loading={pnl.isLoading}>
                  <CustomAgGrid
                    domLayout={'normal'}
                    rowData={pnlData}
                    columnDefs={pnlColDefs(hasWriteAccess)}
                    getRowId={(
                      params: GetRowIdParams<Attributions.GetPNLDiscrepanciesData[number]>,
                    ) =>
                      `${params.data.value_date}-${params.data.fund_name}-${params.data.structurename}`
                    }
                    onCellValueChanged={(
                      params: CellValueChangedEvent<Attributions.GetPNLDiscrepanciesData[number]>,
                    ) => {
                      return updatePnlDiscrepancyMutation.mutateAsync({
                        rowData: params.node.data!,
                        oldValue: params.oldValue,
                        newValue: params.newValue,
                        updatedField: params.colDef.field!,
                      })
                    }}
                  />
                </WidgetContent>
              </Widget>
            </ResizablePanel>
            <ResizableHandle />
            <ResizablePanel defaultSize={40}>
              <Widget exposed className={'h-full'}>
                <WidgetHeaderWithQuery
                  title={`Securities discrepancies - ${values.fund_name}`}
                  query={securities}
                />
                <WidgetContent loading={securities.isLoading}>
                  <CustomAgGrid
                    domLayout={'normal'}
                    rowData={securitiesData}
                    columnDefs={securitiesColDefs}
                  />
                </WidgetContent>
              </Widget>
            </ResizablePanel>
          </ResizablePanelGroup>
        </ResizablePanel>
        <ResizableHandle />
        <ResizablePanel>
          <ResizablePanelGroup direction={'horizontal'}>
            <ResizablePanel>
              <Widget exposed className={'h-full'}>
                <WidgetHeaderWithQuery
                  title={`Chart of Returns from BBG vs Ops vs Rec`}
                  query={chart}
                />
                <WidgetContent loading={chart.isLoading}>
                  <CustomAgChart options={getChartOptions(chartData)} className={'w-full h-full'} />
                </WidgetContent>
              </Widget>
            </ResizablePanel>
            <ResizableHandle />
            <ResizablePanel defaultSize={40}>
              <Widget exposed className={'h-full'}>
                <WidgetHeaderWithQuery
                  title={`Final Attributions - ${values.fund_name}`}
                  query={finalAttributions}
                />
                <WidgetContent loading={finalAttributions.isLoading}>
                  <CustomAgGrid
                    domLayout={'normal'}
                    rowData={finalAttributionsData}
                    columnDefs={finalAttributionsColDefs}
                  />
                </WidgetContent>
              </Widget>
            </ResizablePanel>
          </ResizablePanelGroup>
        </ResizablePanel>
      </ResizablePanelGroup>
    </>
  )
}
